import { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Logo from '../../components/brand/Logo.jsx'
import Button from '../../components/ui/Button.jsx'
import Icon from '../../components/ui/Icon.jsx'
import Badge from '../../components/ui/Badge.jsx'
import { Select } from '../../components/ui/Field.jsx'
import LanguageSwitch from '../../components/ui/LanguageSwitch.jsx'
import { useFeedback } from '../../components/ui/feedback.jsx'
import { useAuth } from '../../context/AuthContext.jsx'
import api from '../../lib/api.js'

/*
 * Applicant-side waiting screen while a company registration is under admin
 * validation. Polls GET /register/status; once active → dashboard. Surfaces the
 * admin's messages and the submitted documents, and lets the applicant add more
 * documents (POST /register/add-document) — e.g. when the admin asks for them.
 */
const DOC_TYPES = ['bce_extract', 'id_card', 'procuration', 'other']
const MAX = 5 * 1024 * 1024

export default function CompanyPendingValidationPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { logout } = useAuth()
  const { toast, notify } = useFeedback()
  const [status, setStatus] = useState(null)
  const [checking, setChecking] = useState(false)
  const [docType, setDocType] = useState('bce_extract')
  const [uploading, setUploading] = useState(false)
  const fileRef = useRef(null)

  const check = useCallback(async () => {
    setChecking(true)
    try {
      const { data } = await api.get('/register/status')
      setStatus(data)
      const step = data?.current_step || data?.status
      if (step === 'active' || data?.is_active) navigate('/app/dashboard')
    } catch { /* keep waiting */ } finally { setChecking(false) }
  }, [navigate])

  useEffect(() => {
    check()
    const id = setInterval(check, 30000) // poll every 30s
    return () => clearInterval(id)
  }, [check])

  const rejected = (status?.current_step || status?.status) === 'validation_rejected'
  const onLogout = () => { logout(); navigate('/login') }

  const messages = (status?.messages || []).slice().reverse() // chronological
  const documents = status?.documents || []

  const onFile = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (file.size > MAX) { notify({ tone: 'error', title: t('pending.docs.error'), message: t('pending.docs.tooBig') }); return }
    setUploading(true)
    try {
      const fd = new FormData()
      fd.append('document_type', docType)
      fd.append('file', file)
      await api.post('/register/add-document', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      toast(t('pending.docs.added'), 'success')
      check()
    } catch (err) { notify({ tone: 'error', title: t('pending.docs.error'), message: err?.response?.data?.error || t('pending.docs.uploadError') }) } finally { setUploading(false) }
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 20px', background: 'radial-gradient(1200px 600px at 80% -10%,#efe9fb,#f6f4fb)' }}>
      <div style={{ position: 'absolute', top: 22, right: 22 }}><LanguageSwitch /></div>
      <div style={{ width: '100%', maxWidth: 560 }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 22 }}><Logo size={34} /></div>

        <div style={{ background: '#fff', borderRadius: 'var(--radius-card-lg)', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-card)', padding: '34px 32px', textAlign: 'center', animation: 'vfade .25s ease' }}>
          <div style={{ width: 58, height: 58, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', background: rejected ? 'var(--color-danger-bg)' : 'var(--color-warning-bg)', color: rejected ? 'var(--color-danger)' : 'var(--color-warning)' }}>
            <Icon name={rejected ? 'alert-triangle' : 'shield'} size={28} />
          </div>
          <h1 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 22, color: 'var(--color-ink)', marginBottom: 8 }}>
            {rejected ? t('pending.rejectedTitle') : t('pending.title')}
          </h1>
          {status?.company?.name && <div style={{ fontWeight: 600, color: 'var(--color-primary)', marginBottom: 8 }}>{status.company.name}</div>}
          <p style={{ color: 'var(--color-text)', fontSize: 14.5, lineHeight: 1.6, marginBottom: rejected && status?.validation_notes ? 14 : 22 }}>
            {rejected ? t('pending.rejectedMessage') : t('pending.message')}
          </p>
          {rejected && status?.validation_notes && (
            <div style={{ background: 'var(--color-danger-bg)', color: 'var(--color-danger)', borderRadius: 10, padding: '10px 14px', fontSize: 13.5, marginBottom: 22, textAlign: 'left' }}>{status.validation_notes}</div>
          )}
          {!rejected && (
            <Button variant="secondary" icon="settings" loading={checking} onClick={check}>{t('pending.refresh')}</Button>
          )}
        </div>

        {/* Messages from the admin */}
        {messages.length > 0 && (
          <Section title={t('pending.messages.title')} icon="mail">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {messages.map((m) => (
                <div key={m.id} style={{ alignSelf: m.is_from_admin ? 'flex-start' : 'flex-end', maxWidth: '85%', background: m.is_from_admin ? 'var(--color-bg-lilac)' : 'var(--color-primary)', color: m.is_from_admin ? 'var(--color-ink)' : '#fff', borderRadius: 12, padding: '10px 14px' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, opacity: 0.7, marginBottom: 3 }}>{m.is_from_admin ? t('pending.messages.fromAdmin') : t('pending.messages.fromYou')} · {fmtTs(m.created_at)}</div>
                  <div style={{ fontSize: 13.5, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{m.message}</div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Documents + add more */}
        {!rejected && (
          <Section title={t('pending.docs.title')} icon="invoice">
            {documents.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
                {documents.map((d) => (
                  <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13.5 }}>
                    <span style={{ color: 'var(--color-success)' }}><Icon name="check" size={16} /></span>
                    <span style={{ flex: 1, color: 'var(--color-ink)' }}>{t(`register.documents.types.${d.type}`, d.type)}</span>
                    <span style={{ fontSize: 12, color: 'var(--color-text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 }}>{d.filename}</span>
                  </div>
                ))}
              </div>
            )}
            <p style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 10 }}>{t('pending.docs.hint')}</p>
            <div style={{ display: 'flex', gap: 8 }}>
              <Select value={docType} onChange={(e) => setDocType(e.target.value)} style={{ flex: 1 }}>
                {DOC_TYPES.map((dt) => <option key={dt} value={dt}>{t(`register.documents.types.${dt}`)}</option>)}
              </Select>
              <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={onFile} style={{ display: 'none' }} />
              <Button variant="secondary" icon="download" loading={uploading} onClick={() => fileRef.current?.click()}>{t('pending.docs.add')}</Button>
            </div>
          </Section>
        )}

        <div style={{ textAlign: 'center', marginTop: 18 }}>
          <button onClick={onLogout} style={{ background: 'none', border: 'none', color: 'var(--color-text-muted)', fontWeight: 600, fontSize: 13.5, cursor: 'pointer' }}>{t('pending.logout')}</button>
        </div>
      </div>
    </div>
  )
}

function Section({ title, icon, children }) {
  return (
    <div style={{ background: '#fff', borderRadius: 'var(--radius-card-lg)', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-card)', padding: '20px 24px', marginTop: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <span style={{ color: 'var(--color-primary)' }}><Icon name={icon} size={18} /></span>
        <h2 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 15, color: 'var(--color-ink)' }}>{title}</h2>
      </div>
      {children}
    </div>
  )
}

function fmtTs(ts) {
  if (!ts) return ''
  const d = new Date(ts)
  return `${d.toLocaleDateString('fr-BE')} ${d.toLocaleTimeString('fr-BE', { hour: '2-digit', minute: '2-digit' })}`
}
